import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";

import * as Api from "../api";
import AppDialog from "./AppDialog";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_SIZE,
  MAX_IMAGES_PER_UPLOAD,
  describeRejections,
} from "./modelImages.constants";

// One saved photo, with a graceful fallback for formats the browser can't paint
// inline (e.g. HEIC — accepted by the backend but not renderable in most browsers).
const ImageThumb = ({ image, deleting, onDelete }) => {
  const [failed, setFailed] = useState(false);

  return (
    <Box sx={styles.thumb}>
      {failed ? (
        <Box sx={styles.thumbFallback}>
          <ImageOutlinedIcon sx={{ color: "#9AA89A", fontSize: 26 }} />
          <Typography sx={styles.thumbFallbackText} noWrap>
            {image.originalName || "image"}
          </Typography>
        </Box>
      ) : (
        <Box
          component="img"
          src={image.url}
          alt={image.originalName || "model photo"}
          title={image.originalName || ""}
          onError={() => setFailed(true)}
          sx={styles.thumbImg}
        />
      )}

      <Box sx={styles.thumbOverlay} className="thumb-overlay">
        <IconButton
          size="small"
          onClick={() => onDelete(image)}
          disabled={deleting}
          sx={styles.deleteBtn}
          aria-label="delete photo"
        >
          {deleting ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            <DeleteOutlineIcon sx={{ fontSize: 18, color: "#fff" }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

/**
 * Self-contained input-photo manager for a single 3D model: lists, uploads
 * (append-only) and deletes photos via the SDK images API. Used inline on the
 * model detail page and inside ModelImagesDialog on the AR Models list.
 */
const ModelImagesPanel = ({ modelId, onNotify, showTitle = true, title = "Photos" }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, image: null });
  // Ignore responses for a model we've navigated away from.
  const activeRef = useRef(null);

  const notify = (message, msgType) => onNotify && onNotify(message, msgType);

  const fetchImages = useCallback(async () => {
    if (!modelId) return;
    const reqModel = modelId;
    setLoading(true);
    const res = await Api.getModelImages(reqModel);
    if (activeRef.current !== reqModel) return; // stale
    if (res?.success) {
      setImages(res.data?.rows || []);
    } else {
      notify(res?.msg || "Failed to load photos", "error");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);

  useEffect(() => {
    activeRef.current = modelId || null;
    setConfirm({ open: false, image: null });
    setDeletingId(null);
    setUploading(false);
    setImages([]);
    if (modelId) fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);

  const handleUpload = async (files) => {
    if (!files?.length || !modelId) return;
    const reqModel = modelId;
    setUploading(true);
    const res = await Api.uploadModelImages(reqModel, files);
    if (activeRef.current !== reqModel) return; // stale
    if (res?.success) {
      // Response carries the full, current list (append-only) — use it directly.
      setImages(res.data?.rows || []);
      notify(
        `${files.length} photo${files.length > 1 ? "s" : ""} uploaded`,
        "success"
      );
    } else {
      notify(res?.msg || "Failed to upload photos", "error");
    }
    setUploading(false);
  };

  const { getRootProps, getInputProps, isDragActive, open: openFileDialog } =
    useDropzone({
      accept: ACCEPTED_IMAGE_TYPES,
      maxSize: MAX_IMAGE_SIZE,
      maxFiles: MAX_IMAGES_PER_UPLOAD,
      multiple: true,
      disabled: uploading,
      noClick: true,
      noKeyboard: true,
      onDropAccepted: handleUpload,
      onDropRejected: (rej) => {
        const msg = describeRejections(rej);
        if (msg) notify(msg, "error");
      },
    });

  const handleDeleteConfirmed = async () => {
    const image = confirm.image;
    if (!image || !modelId) return;
    const reqModel = modelId;
    setDeletingId(image._id);
    const res = await Api.deleteModelImage(reqModel, image._id);
    if (activeRef.current !== reqModel) return; // stale
    if (res?.success) {
      setImages(res.data?.rows || []);
    } else if (res?.msg?.toLowerCase().startsWith("image not found")) {
      // Benign per spec §4: already gone. Silently re-sync.
      await fetchImages();
    } else {
      notify(res?.msg || "Failed to delete photo", "error");
    }
    setDeletingId(null);
    setConfirm({ open: false, image: null });
  };

  return (
    <Box>
      {showTitle ? <Typography sx={styles.panelTitle}>{title}</Typography> : null}

      <Box
        {...getRootProps()}
        sx={{
          ...styles.dropzone,
          borderColor: isDragActive ? "#7CB342" : "#CFE0BE",
          backgroundColor: isDragActive ? "#F1F8E9" : "#FAFCF7",
          cursor: uploading ? "default" : "pointer",
          opacity: uploading ? 0.7 : 1,
        }}
        onClick={uploading ? undefined : openFileDialog}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <CircularProgress size={24} sx={{ color: "#558B2F" }} />
        ) : (
          <CloudUploadOutlinedIcon sx={{ fontSize: 30, color: "#7CB342" }} />
        )}
        <Typography sx={styles.dropTitle}>
          {uploading
            ? "Uploading…"
            : isDragActive
            ? "Drop photos to upload"
            : "Drag & drop photos here, or click to browse"}
        </Typography>
        <Typography sx={styles.dropHint}>
          JPEG, PNG, HEIC, HEIF or WEBP · up to 15&nbsp;MB each · 50 at a time
        </Typography>
      </Box>

      <Box sx={styles.galleryHeader}>
        <Typography sx={styles.galleryCount}>
          {loading
            ? "Loading…"
            : `${images.length} photo${images.length === 1 ? "" : "s"}`}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={styles.centerBox}>
          <CircularProgress size={26} sx={{ color: "#558B2F" }} />
        </Box>
      ) : images.length === 0 ? (
        <Box sx={styles.emptyBox}>
          <CollectionsOutlinedIcon sx={{ fontSize: 38, color: "#C3D1B6" }} />
          <Typography sx={styles.emptyText}>No photos yet</Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          {images.map((image) => (
            <Grid item xs={6} sm={4} key={image._id}>
              <ImageThumb
                image={image}
                deleting={deletingId === image._id}
                onDelete={(img) => setConfirm({ open: true, image: img })}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AppDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false, image: null })}
        title="Remove photo?"
        content={
          confirm.image?.originalName
            ? `"${confirm.image.originalName}" will be permanently removed from this model.`
            : "This photo will be permanently removed from this model."
        }
        firstBtnContent="Cancel"
        secondBtnContent={deletingId ? "Removing…" : "Remove"}
        firstBtnClickHandler={() => setConfirm({ open: false, image: null })}
        secondBtnClickHandler={handleDeleteConfirmed}
        options={{ keepMounted: false, btnLoading: !!deletingId }}
      />
    </Box>
  );
};

export default ModelImagesPanel;

const styles = {
  panelTitle: {
    fontFamily: "GilroyBold",
    fontSize: 16,
    color: "#1F2A1A",
    mb: 1.5,
  },
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: 0.75,
    border: "1.5px dashed",
    borderRadius: 3,
    px: 2,
    py: 3,
    transition: "background-color .15s ease, border-color .15s ease",
  },
  dropTitle: {
    fontFamily: "GilroySemiBold",
    fontSize: 13.5,
    color: "#3F4B38",
  },
  dropHint: {
    fontFamily: "GilroyRegular",
    fontSize: 11.5,
    color: "#8A9881",
  },
  galleryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mt: 2,
    mb: 0.5,
  },
  galleryCount: {
    fontFamily: "GilroySemiBold",
    fontSize: 13,
    color: "#5B6B52",
  },
  centerBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    py: 4,
  },
  emptyBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    py: 4,
  },
  emptyText: {
    fontFamily: "GilroySemiBold",
    fontSize: 14,
    color: "#5B6B52",
    mt: 0.5,
  },
  thumb: {
    position: "relative",
    width: "100%",
    paddingTop: "100%",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid #E3E9DD",
    backgroundColor: "#F4F7F2",
    "&:hover .thumb-overlay": { opacity: 1 },
  },
  thumbImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  thumbFallback: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    px: 1,
  },
  thumbFallbackText: {
    fontFamily: "GilroyRegular",
    fontSize: 11,
    color: "#7A8A72",
    maxWidth: "100%",
  },
  thumbOverlay: {
    position: "absolute",
    top: 6,
    right: 6,
    opacity: 0,
    transition: "opacity .15s ease",
  },
  deleteBtn: {
    backgroundColor: "rgba(0,0,0,0.55)",
    "&:hover": { backgroundColor: "rgba(211,47,47,0.9)" },
  },
};
